import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

import { useLanguage } from '../../i18n/language.js';
import { fetchProviders } from '../../workspace/api.js';
import { browserNavigation } from '../../workspace/navigation.js';

export function MerchantAuthModal({
  close,
  intent,
}: {
  readonly close: () => void;
  readonly intent: 'confirm' | 'correct';
}): ReactNode {
  const { copy } = useLanguage();
  const [providers, setProviders] = useState<readonly string[] | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let active = true;
    void fetchProviders()
      .then((result) => {
        if (active) setProviders(result.providers);
      })
      .catch(() => {
        if (active) setProviders([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    return () => {
      returnFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [close]);

  const title =
    intent === 'correct'
      ? copy.merchantAuth.correctTitle
      : copy.merchantAuth.claimTitle;
  const body =
    intent === 'correct' ? copy.merchantAuth.correctBody : copy.merchantAuth.claimBody;

  return (
    <div
      className="mj-merchant-auth-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="merchant-auth-title"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) close();
      }}
    >
      <div className="mj-merchant-auth" data-surface="landing-light">
        <button
          ref={closeRef}
          type="button"
          aria-label={copy.merchantAuth.close}
          onClick={close}
          className="mj-merchant-auth-close"
        >
          <X size={18} aria-hidden="true" />
        </button>
        <div className="mj-merchant-auth-body">
          <h2 id="merchant-auth-title">{title}</h2>
          <p>{body}</p>
        </div>
        <div className="mj-merchant-auth-actions">
          {providers?.includes('google') ? (
            <ProviderButton label={copy.merchantAuth.google} provider="google" />
          ) : null}
          {providers?.includes('apple') ? (
            <ProviderButton label={copy.merchantAuth.apple} provider="apple" />
          ) : null}
          {providers === null ? (
            <p role="status" className="mj-merchant-auth-status">
              {copy.merchantAuth.loading}
            </p>
          ) : null}
          {providers?.length === 0 ? (
            <p role="alert" className="mj-merchant-auth-warning">
              {copy.merchantAuth.unavailable}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProviderButton({
  label,
  provider,
}: {
  readonly label: string;
  readonly provider: string;
}) {
  return (
    <button
      type="button"
      className="mj-merchant-auth-provider"
      aria-label={label}
      onClick={() => {
        browserNavigation.assign(`/auth/merchant/${provider}/start`);
      }}
    >
      {provider === 'google' ? <GoogleMark /> : null}
      <span>{label}</span>
    </button>
  );
}

function GoogleMark(): ReactNode {
  return (
    <svg
      className="mj-google-mark"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.716v2.259h2.909c1.702-1.567 2.684-3.875 2.684-6.616Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.468-.806 5.956-2.179l-2.909-2.259c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.037-3.712H.956v2.333A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.963 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.169.281-1.71V4.957H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.043l3.007-2.333Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.578c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.957L3.963 7.29C4.672 5.162 6.656 3.578 9 3.578Z"
      />
    </svg>
  );
}
